import * as React from 'react'
import { useCallback, useMemo } from 'react'

import { type SelectOption } from '@factorialco/deprecated-design-system'
import _ from 'lodash'
import type moment from 'moment'

import { observer } from 'mobx-react'

import FormRow from 'modules/core/components/FormRow'
import DayPicker from 'modules/core/components/Inputs/DayPicker'
import InformationTooltip from 'modules/core/components/Inputs/InformationTooltip'
import Select from 'modules/core/components/Inputs/Select'
import Text from 'modules/core/components/Inputs/Text'
import TextField from 'modules/core/components/TextField'
import { stringToDate } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import { type ContractVersions } from 'modules/contracts/store/collections/contractVersions'
import documents from 'modules/documents/store/collections/documents'
import type DocumentModel from 'modules/documents/store/models/Document'
import DocumentUploaderWithLinks from 'modules/timeoff/components/DocumentUploaderWithLinks'
import type LeaveUpdateForm from 'modules/timeoff/lib/LeaveUpdateForm'
import { getMedicalReason } from 'modules/timeoff/lib/getMedicalReason'
import leaves from 'modules/timeoff/store/collections/leaves'
import MedicalLeaveUpdate from 'modules/timeoff/store/models/MedicalLeaveUpdate'

const getDropZoneLabelLabel = (type: string) => {
  switch (type) {
    case 'medical_confirmation':
      return i18n.t('time_off.medical_leave.updates.document.drop_zone_label.medical_confirmation')
    case 'medical_discharge':
      return i18n.t('time_off.medical_leave.updates.document.drop_zone_label.medical_discharge')
    case 'medical_hospital_stay':
      return i18n.t('time_off.medical_leave.updates.document.drop_zone_label.medical_hospital_stay')
    default:
      throw new Error(`Unknown translation for type: ${type}`)
  }
}

const getTypesLabel = (type: string) => {
  switch (type) {
    case 'medical_confirmation':
      return i18n.t('time_off.medical_leave.updates.types.medical_confirmation')
    case 'medical_discharge':
      return i18n.t('time_off.medical_leave.updates.types.medical_discharge')
    case 'medical_hospital_stay':
      return i18n.t('time_off.medical_leave.updates.types.medical_hospital_stay')
    case 'medical_leave_start':
      return i18n.t('time_off.medical_leave.updates.types.medical_leave_start')
    default:
      throw new Error(`Unknown translation for type: ${type}`)
  }
}

interface Props {
  form: LeaveUpdateForm
  minEffectiveOn: moment.Moment
  leaveUpdate?: MedicalLeaveUpdate | null | undefined
  medicalLeaveType: string | null | undefined
  readOnly?: boolean
  contractVersions: ContractVersions
}

const Content: React.FC<Props> = (props) => {
  const { form, readOnly, leaveUpdate, minEffectiveOn, medicalLeaveType, contractVersions } = props

  const documentModel = useMemo((): DocumentModel | null | undefined => {
    if (!leaveUpdate) return null
    return documents.find({ id: leaveUpdate.attributes.document.id })
  }, [leaveUpdate])

  const leaveUpdateType = form.get('leave_update_type').value

  const isHospitalization = useMemo(() => {
    return leaveUpdateType === MedicalLeaveUpdate.types.medical_hospital_stay
  }, [leaveUpdateType])

  const isDischarge = useMemo(() => {
    return leaveUpdateType === MedicalLeaveUpdate.types.medical_discharge
  }, [leaveUpdateType])

  const enabledOptions = useCallback((type: string): boolean => {
    return type !== MedicalLeaveUpdate.types.medical_hospital_stay
  }, [])

  const options: SelectOption[] = useMemo(() => {
    return _.keys(MedicalLeaveUpdate.types)
      .filter(enabledOptions)
      .map((value) => ({
        value,
        label: getTypesLabel(value ?? ''),
      }))
  }, [enabledOptions])

  const dischargeReasonsOptions: SelectOption[] = useMemo(() => {
    return _.keys(MedicalLeaveUpdate.dischargeReasons).map((value) => ({
      value,
      label: getMedicalReason(value ?? ''),
    }))
  }, [])

  const onDeleteDocument = useCallback(() => {
    if (!leaveUpdate) return
    leaveUpdate.set({ document: {} })
  }, [leaveUpdate])

  const renderType = () => {
    const label = i18n.t('time_off.medical_leave.updates.type_label')
    const field = form.get('leave_update_type')
    const value = getTypesLabel(field.value ?? '')

    if (leaveUpdate) {
      return <TextField readonly label={label} value={value} />
    }

    return <Select label={label} field={field} options={options} />
  }

  const renderDateAndColegiate = () => {
    const dischargeFull = !!medicalLeaveType && leaves.isDischargeFull(medicalLeaveType)
    return (
      <FormRow multi>
        <DayPicker
          field={form.get('effective_on')}
          label={i18n.t('time_off.medical_leave.updates.effective_on_label')}
          minDate={minEffectiveOn}
          readonly={readOnly}
        />
        {isHospitalization && (
          <DayPicker
            field={form.get('hospital_finish_on')}
            label={i18n.t('time_off.medical_leave.updates.finish_hospitalization_label')}
            minDate={stringToDate(form.get('effective_on').value, 'ui')}
            readonly={readOnly}
          />
        )}
        {!isHospitalization && dischargeFull && (
          <Text
            field={form.get('colegiate_number')}
            label={i18n.t('time_off.medical_leave.colegiate_number.label')}
            secondaryLabel={
              <InformationTooltip
                information={i18n.t('time_off.medical_leave.colegiate_number.information')}
                icon="question"
              />
            }
            readonly={readOnly}
          />
        )}
      </FormRow>
    )
  }

  const renderDischargeReason = () => {
    const isDischargeFull = medicalLeaveType && leaves.isDischargeFull(medicalLeaveType)

    if (!isDischarge || !isDischargeFull) {
      return null
    }

    const label = i18n.t('time_off.medical_leave.discharge_reason.label')
    const field = form.get('medical_discharge_reason')
    const value = getMedicalReason(field.value ?? '')

    if (readOnly) {
      return (
        <FormRow>
          <TextField readonly label={label} value={value} />
        </FormRow>
      )
    }

    return (
      <FormRow>
        <Select label={label} field={field} options={dischargeReasonsOptions} />
      </FormRow>
    )
  }

  const documentPlaceholder = () => {
    const type = form.get('leave_update_type').value
    return getDropZoneLabelLabel(type ?? '')
  }

  return (
    <>
      <FormRow>{renderType()}</FormRow>
      {renderDateAndColegiate()}
      {renderDischargeReason()}
      <FormRow>
        <DocumentUploaderWithLinks
          document={documentModel}
          field={form.get('document')}
          onDeleteDocument={onDeleteDocument}
          placeholder={documentPlaceholder()}
          readOnly={readOnly}
          contractVersions={contractVersions}
        />
      </FormRow>
    </>
  )
}

export default observer(Content)
